import React, { useEffect } from 'react';

import energyIcon from 'assets/pics/energy_icon.svg';
import gasIcon from 'assets/pics/gas_icon.svg';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';

import { CustomModal } from 'components/CustomModal';
import { getDEExpertPopupDeleteErrorMessage } from 'components/DEBForm/Hooks/utils';
import { YExpertDelete } from 'components/DEBForm/ValidationStructure/YExpert';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormField } from 'components/FormHelper/FormField';
import { LabelCheckbox } from 'components/LabelCheckbox/LabelCheckbox';

import {
  BRANCH_TYPE,
  EIV_STATUS_ALLOW_EXPERT_EDITING,
  EIV_STATUS_NOT_RELEVANT,
} from 'global/constants';

import { FFormDebug } from 'utils/FFormDebug';

import './Popup.sass';

export const ExpertPopupDelete = ({
  mainFormik,
  selectedRow,
  popupOpen,
  setPopupOpen,
  deleteExpert,
  installerStatusSummary,
}) => {
  const formik = useFormik({
    validationSchema: YExpertDelete,
    initialValues: {
      deleteEnergy: false,
      deleteGas: false,
      deletionReasonEnergy: '',
      deletionDateEnergy: '',
      deletionReasonGas: '',
      deletionDateGas: '',
    },
  });

  const expert = mainFormik.values.experts[selectedRow];

  const showEnergy =
    EIV_STATUS_ALLOW_EXPERT_EDITING.includes(installerStatusSummary[BRANCH_TYPE.ENERGY].status) &&
    !EIV_STATUS_NOT_RELEVANT.includes(expert?.statusEnergy);
  const showGas =
    EIV_STATUS_ALLOW_EXPERT_EDITING.includes(installerStatusSummary[BRANCH_TYPE.GAS].status) &&
    !EIV_STATUS_NOT_RELEVANT.includes(expert?.statusGas);

  useEffect(() => {
    if (popupOpen && selectedRow !== null) {
      if (!expert) {
        return;
      }

      formik.setValues({
        deleteEnergy: showEnergy,
        deleteGas: showGas,
        expertStatusEnergy: expert.statusEnergy,
        expertStatusGas: expert.statusGas,
        deletionReasonEnergy: expert.deletionReasonEnergy || '',
        deletionDateEnergy: expert.deletionDateEnergy || '',
        deletionReasonGas: expert.deletionReasonGas || '',
        deletionDateGas: expert.deletionDateGas || '',
      });
      formik.setErrors({ noBranchSelected: 'Bitte wählen Sie eine Sparte' });
    }
  }, [popupOpen, selectedRow]);

  useEffect(() => {
    if (!formik.values.deleteEnergy && !formik.values.deleteGas) {
      formik.setErrors({ noBranchSelected: 'Bitte wählen Sie eine Sparte' });
    }
  }, [formik.values.deleteEnergy, formik.values.deleteGas]);

  const onCancel = () => {
    setPopupOpen(false);
  };
  const onSubmit = () => {
    const deleteBranches = [];

    const calcUpdateEnergyObject = (base = {}) => {
      if (!formik.values.deleteEnergy) {
        return base;
      }

      deleteBranches.push(BRANCH_TYPE.ENERGY);

      return {
        ...base,
        deletionReasonEnergy: formik.values.deletionReasonEnergy,
        deletionDateEnergy: formik.values.deletionDateEnergy,
      };
    };

    const calcUpdateGasObject = (base = {}) => {
      if (!formik.values.deleteGas) {
        return base;
      }

      deleteBranches.push(BRANCH_TYPE.GAS);

      return {
        ...base,
        deletionReasonGas: formik.values.deletionReasonGas,
        deletionDateGas: formik.values.deletionDateGas,
      };
    };

    deleteExpert(expert.id, calcUpdateGasObject(calcUpdateEnergyObject()), deleteBranches);

    setPopupOpen(false);
  };

  // check no experts and mobile view
  const modalContent = () => {
    if (mainFormik.values.experts && mainFormik.values.experts[`${selectedRow}`])
      return (
        <div className="popup-content">
          <div className="popup-content__popup-subtext">
            Möchten Sie die Fachkraft {mainFormik.values.experts[`${selectedRow}`].firstName}{' '}
            {mainFormik.values.experts[`${selectedRow}`].lastName} wirklich entfernen?
          </div>
          <div className="ne-popup-field">
            <div className="flex-row">
              <div>Für die Sparte:</div>
              {showEnergy && (
                <LabelCheckbox
                  label="Strom"
                  id="deleteEnergy"
                  name="deleteEnergy"
                  disabled={!showGas}
                  error={Boolean(formik.errors?.noBranchSelected)}
                  icon={energyIcon}
                  value={formik.values.deleteEnergy}
                  onChange={formik.handleChange}
                />
              )}
              {showGas && (
                <LabelCheckbox
                  label="Gas"
                  id="deleteGas"
                  name="deleteGas"
                  disabled={!showEnergy}
                  error={Boolean(formik.errors?.noBranchSelected)}
                  icon={gasIcon}
                  value={formik.values.deleteGas}
                  onChange={formik.handleChange}
                />
              )}
            </div>
          </div>
          {formik.values.deleteEnergy && (
            <>
              <div className="popup-content__popup-field">
                <FormField
                  name="deletionReasonEnergy"
                  formik={formik}
                  label="Grund (Strom)"
                  oldValue=""
                  fullWidth
                />
              </div>
              <div className="popup-content__popup-field">
                <FormField
                  name="deletionDateEnergy"
                  formik={formik}
                  label="Austritt am (Strom)"
                  oldValue=""
                  fullWidth
                />
              </div>
            </>
          )}
          {formik.values.deleteGas && (
            <>
              <div className="popup-content__popup-field">
                <FormField
                  name="deletionReasonGas"
                  formik={formik}
                  label="Grund (Gas)"
                  oldValue=""
                  fullWidth
                />
              </div>
              <div className="popup-content__popup-field">
                <FormField
                  name="deletionDateGas"
                  formik={formik}
                  label="Austritt am (Gas)"
                  oldValue=""
                  fullWidth
                />
              </div>
            </>
          )}
          <FFormDebug values={formik.values} errors={formik.errors} />
        </div>
      );

    return <>Error</>;
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="Fachkraft entfernen"
      closeCancelEventModal={onCancel}
      ctaButtons={
        <FormActionButtons
          hasErrors={Object.keys(formik.errors).length > 0}
          titleLeft="Abbrechen"
          titleRight="Ok"
          onClickLeft={onCancel}
          onClickRight={onSubmit}
          errorMessage={getDEExpertPopupDeleteErrorMessage(formik)}
        />
      }
      content={modalContent()}
    />
  );
};
ExpertPopupDelete.propTypes = {
  mainFormik: PropTypes.object.isRequired,
  selectedRow: PropTypes.number,
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  deleteExpert: PropTypes.func,
  installerStatusSummary: PropTypes.object,
};
ExpertPopupDelete.defaultProps = {
  selectedRow: 0,
  popupOpen: false,
  setPopupOpen: () => {},
  deleteExpert: () => {},
  installerStatusSummary: {},
};
